/**
 * Before/after comparison for a card's screenshots.
 *
 * The tester attaches deterministic before/after captures of the same
 * viewport; reading them as two separate thumbnails hides exactly the thing
 * the pair exists to show — the difference. This lays one over the other and
 * lets a slider sweep between them.
 *
 * The slider is a real `<input type="range">` stretched invisibly over the
 * stage: pointer drags, clicks and arrow keys all come for free, and the
 * screen reader announces a percentage rather than nothing. Primer has no
 * comparison component (noted gap); everything here is styled with
 * primitives variables only.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Select } from '@primer/react';

import type { BoardAttachmentView } from '../../client/types.js';
import { matchingImageProportions, type ImageDimensions } from '../../logic/image-comparison.js';
import { useAttachment } from './attachment.js';
import { Orb } from './orb.js';

function isImage(attachment: BoardAttachmentView): boolean {
  return attachment.mediaType.startsWith('image/');
}

/** The default pair: the first `before*` against the last `after*`. */
export function comparablePair(
  attachments: readonly BoardAttachmentView[],
): { before: BoardAttachmentView; after: BoardAttachmentView } | null {
  const images = attachments.filter(isImage);
  const befores = images.filter((attachment) => attachment.filename.toLowerCase().includes('before'));
  const afters = images.filter((attachment) => attachment.filename.toLowerCase().includes('after'));
  const before = befores[0];
  const after = afters[afters.length - 1];
  if (before === undefined || after === undefined) return null;
  return { before, after };
}

export function BeforeAfterCompare({ attachments }: { attachments: readonly BoardAttachmentView[] }): ReactNode {
  const images = useMemo(() => attachments.filter(isImage), [attachments]);
  const pair = useMemo(() => comparablePair(attachments), [attachments]);

  const [beforeId, setBeforeId] = useState<string | null>(null);
  const [afterId, setAfterId] = useState<string | null>(null);
  const [position, setPosition] = useState(50);

  if (pair === null) return null;

  const before = images.find((attachment) => attachment.id === beforeId) ?? pair.before;
  const after = images.find((attachment) => attachment.id === afterId) ?? pair.after;

  return (
    <div className="compare">
      <div className="compare-pickers">
        <Select
          aria-label="Before image"
          size="small"
          value={before.id}
          onChange={(event) => setBeforeId(event.target.value)}
        >
          {images.map((attachment) => (
            <Select.Option value={attachment.id} key={attachment.id}>
              {attachment.filename}
            </Select.Option>
          ))}
        </Select>
        <span className="compare-vs">against</span>
        <Select
          aria-label="After image"
          size="small"
          value={after.id}
          onChange={(event) => setAfterId(event.target.value)}
        >
          {images.map((attachment) => (
            <Select.Option value={attachment.id} key={attachment.id}>
              {attachment.filename}
            </Select.Option>
          ))}
        </Select>
      </div>
      <Stage before={before} after={after} position={position} onPosition={setPosition} />
    </div>
  );
}

function Stage({
  before,
  after,
  position,
  onPosition,
}: {
  before: BoardAttachmentView;
  after: BoardAttachmentView;
  position: number;
  onPosition: (next: number) => void;
}): ReactNode {
  const beforeLoaded = useAttachment(before);
  const afterLoaded = useAttachment(after);
  const beforeDimensions = useImageDimensions(beforeLoaded?.url ?? null);
  const afterDimensions = useImageDimensions(afterLoaded?.url ?? null);

  if (beforeLoaded === null || afterLoaded === null || beforeDimensions === null || afterDimensions === null) {
    return <p className="compare-loading"><Orb size={20} /> Loading both captures…</p>;
  }

  if (!matchingImageProportions(beforeDimensions, afterDimensions)) {
    return <p className="compare-unavailable">These captures have different proportions and are shown separately below.</p>;
  }

  const ratio = beforeDimensions.width / beforeDimensions.height;
  const maxWidth = Math.min(1152, Math.round(720 * ratio));

  return (
    <div className="compare-stage" style={{ aspectRatio: `${String(beforeDimensions.width)} / ${String(beforeDimensions.height)}`, maxWidth }}>
      <img className="compare-base" src={afterLoaded.url} alt={`After: ${after.filename}`} />
      <div className="compare-clip" style={{ clipPath: `inset(0 ${String(100 - position)}% 0 0)` }} aria-hidden="true">
        <img className="compare-top" src={beforeLoaded.url} alt="" />
      </div>
      <div className="compare-handle" style={{ left: `${String(position)}%` }} aria-hidden="true">
        <span className="compare-grip" />
      </div>
      <span className="compare-tag compare-tag-before">Before</span>
      <span className="compare-tag compare-tag-after">After</span>
      <input
        className="compare-range"
        type="range"
        min={0}
        max={100}
        step={1}
        value={position}
        onChange={(event) => onPosition(Number(event.target.value))}
        aria-label={`Reveal of ${before.filename} over ${after.filename}`}
        aria-valuetext={`${String(position)}% before`}
      />
    </div>
  );
}

function useImageDimensions(url: string | null): ImageDimensions | null {
  const [dimensions, setDimensions] = useState<(ImageDimensions & { readonly url: string }) | null>(null);

  useEffect(() => {
    if (url === null) {
      setDimensions(null);
      return undefined;
    }
    let live = true;
    const image = new Image();
    image.onload = () => {
      if (live) setDimensions({ url, width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      if (live) setDimensions(null);
    };
    image.src = url;
    return () => {
      live = false;
    };
  }, [url]);

  return dimensions?.url === url ? dimensions : null;
}
